import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Download, Phone, Globe, ShoppingBag, Wand2, RefreshCw } from "lucide-react";

const PLATFORM_ORDER = ["wordpress", "shopify", "magento", "bigcommerce", "php", "nodejs", "static", "wix", "squarespace", "other"];

export default function DeployColumn({ agentId, ecom, onScan, scanning, refreshEcom }) {
  const [kit, setKit] = useState(null);
  const [active, setActive] = useState(null);
  const [activeChannels, setActiveChannels] = useState({ web_chat: true, web_voice: true });

  const load = async () => {
    try {
      const { data } = await api.get(`/integration-kit/${agentId}`);
      setKit(data);
      if (!active) {
        const preferred = ecom?.platform && PLATFORM_ORDER.includes(ecom.platform) ? ecom.platform : "static";
        setActive(preferred);
      }
    } catch (err) { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agentId, ecom?.platform]);

  if (!kit) return (
    <div className="space-y-3" data-testid="builder-col-deploy">
      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#030712]">
        <div className="font-display font-extrabold text-lg uppercase tracking-tight">Deploy</div>
        <div className="text-[10px] font-mono text-[#4B5563]">paste &amp; go live</div>
      </div>
      <div className="hs-card p-4 text-xs text-[#4B5563]">Loading integration kit…</div>
    </div>
  );

  const platforms = kit.platforms || {};
  const current = active && platforms[active];

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  const buildSnippet = () => {
    if (!kit.base_snippet) return kit.base_snippet;
    const enabled = Object.entries(activeChannels).filter(([_, v]) => v).map(([k]) => k);
    const chList = enabled.length ? enabled.join(",") : "web_chat";
    return kit.base_snippet.replace(/data-channels="[^"]+"/, `data-channels="${chList}"`);
  };

  return (
    <div className="space-y-3" data-testid="builder-col-deploy">
      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#030712]">
        <div className="font-display font-extrabold text-lg uppercase tracking-tight">Deploy</div>
        <div className="text-[10px] font-mono text-[#4B5563]">paste &amp; go live</div>
      </div>

      {/* eCommerce status + scan */}
      {ecom?.site_type === "ecommerce" && (
        <div className="hs-card p-3">
          <div className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563] flex items-center gap-1.5"><ShoppingBag className="w-3 h-3" /> Store data</div>
          <div className="mt-1.5 text-xs">
            <div className="font-medium">{ecom.store_url}</div>
            <div className="text-[#4B5563] text-[11px]">Platform: <span className="font-mono">{ecom.platform}</span> · Strategy: <span className="font-mono">{ecom.scan_strategy}</span></div>
            <div className="text-[#4B5563] text-[11px] mt-0.5">Cached: <span className="font-mono">{ecom.cached_count || 0}</span> products</div>
          </div>
          <button data-testid="ecom-scan-button" onClick={onScan} disabled={scanning} className="btn-outline text-xs w-full mt-2.5 flex items-center justify-center gap-1.5 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning products…" : ecom.cached_count ? "Re-scan products" : "Scan products now"}
          </button>
          <div className="text-[10px] text-[#4B5563] mt-1">Universal sitemap + JSON-LD scan — works without API keys.</div>
        </div>
      )}

      {/* Channel activation */}
      {kit.base_snippet && (
        <div className="hs-card p-3">
          <div className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Activate on website</div>
          <div className="flex gap-2 mt-2">
            <label className="flex-1 flex items-center gap-1.5 text-xs border border-[#E2E8F0] px-2 py-1.5 cursor-pointer">
              <input type="checkbox" data-testid="deploy-toggle-web_chat" checked={activeChannels.web_chat} onChange={(e) => setActiveChannels({ ...activeChannels, web_chat: e.target.checked })} />
              Chat
            </label>
            <label className="flex-1 flex items-center gap-1.5 text-xs border border-[#E2E8F0] px-2 py-1.5 cursor-pointer">
              <input type="checkbox" data-testid="deploy-toggle-web_voice" checked={activeChannels.web_voice} onChange={(e) => setActiveChannels({ ...activeChannels, web_voice: e.target.checked })} />
              Voice
            </label>
          </div>
        </div>
      )}

      {/* Platform tabs */}
      <div className="hs-card p-0 overflow-hidden">
        <div className="grid grid-cols-5 border-b border-[#E2E8F0] text-[10px]">
          {PLATFORM_ORDER.slice(0, 5).map((p) => (
            <button key={p} onClick={() => setActive(p)} data-testid={`deploy-tab-${p}`} className={`px-1 py-2 font-medium truncate ${active === p ? "bg-[#0044FF] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6]"}`}>
              {platforms[p]?.label?.split(" ")[0] || p}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-5 border-b border-[#E2E8F0] text-[10px]">
          {PLATFORM_ORDER.slice(5).map((p) => (
            <button key={p} onClick={() => setActive(p)} data-testid={`deploy-tab-${p}`} className={`px-1 py-2 font-medium truncate ${active === p ? "bg-[#0044FF] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6]"}`}>
              {platforms[p]?.label?.split(" ")[0] || p}
            </button>
          ))}
        </div>

        {current && (
          <div className="p-3 space-y-3">
            <div className="font-display font-bold text-sm flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> {current.label}</div>
            <ol className="text-[11px] text-[#4B5563] space-y-1 list-decimal pl-4">
              {current.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>

            {current.plugin_url && (
              <a href={current.plugin_url} target="_blank" rel="noopener noreferrer" className="btn-primary text-xs flex items-center justify-center gap-1.5" data-testid="deploy-download-plugin">
                <Download className="w-3 h-3" /> Download WordPress plugin (.zip)
              </a>
            )}

            {kit.base_snippet ? (
              <>
                <div className="text-[10px] font-mono uppercase text-[#4B5563]">Snippet</div>
                <div className="font-mono text-[10px] bg-[#0B1220] text-[#94f9c0] p-3 break-all rounded-sm leading-relaxed" data-testid="deploy-snippet">{buildSnippet()}</div>
                <button onClick={() => copy(buildSnippet())} className="btn-outline text-xs w-full flex items-center justify-center gap-1.5" data-testid="deploy-copy-snippet">
                  <Copy className="w-3 h-3" /> Copy snippet
                </button>
              </>
            ) : (
              <div className="text-[11px] text-[#4B5563] italic">Enable Web Chat or Web Voice to see the embed snippet.</div>
            )}
          </div>
        )}
      </div>

      {/* Phone Calling has moved to Calls & Campaigns → Assistants — the Web
          Chat / Web Voice agent is intentionally decoupled from outbound
          telecom so users don't confuse widget deploy with dialer setup. */}

      {/* Smart wizard re-launch */}
      <button onClick={refreshEcom} className="btn-outline text-[11px] w-full flex items-center justify-center gap-1.5" data-testid="deploy-relaunch-wizard">
        <Wand2 className="w-3 h-3" /> Update store / platform settings
      </button>
    </div>
  );
}
